import ExpandLess from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, useTheme } from "@mui/material/styles";
import * as React from "react";
import PrimarySearchAppBar from "../../Appbar";

import ExpandMore from "@mui/icons-material/ExpandMore";
import { Badge, useMediaQuery } from "@mui/material";
import {
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "src/shared/context/useAuth";
import ConditionalWrapper from "../../ConditionalWrapper";
import Footer from "../../footer";
import GlobalMessage from "../../GlobalMessage";
import { CompanyInfo } from "src/shared/Layouts/CompanyInfo";
import { DemoEnvMsg } from "src/shared/Layouts/DemoEnvMsg";

const drawerWidth = 240;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  width: "100vw",
  mb: 10,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  position: "sticky",
  top: "0",
  backgroundColor: "white",
  zIndex: 1000,
  ...theme.mixins.toolbar,
  justifyContent: "space-between",
}));

export const FooterWrapper = DrawerHeader;

const buildListLeaf = (option: {
  label: string;
  level: number;
  icon?: JSX.Element;
  link: string;
  externalLink?: boolean;
  badge?: number;
}) => {
  const navigate = useNavigate();
  return (
    <>
      <ConditionalWrapper
        condition={!option.externalLink}
        wrapper={(children: JSX.Element) => (
          <div style={{ textDecoration: "none", color: "black" }}>
            {children}
          </div>
        )}
      >
        <ListItemButton
          onAuxClick={() => window.open(option.link, "_blank")}
          tabIndex={12}
          onClick={() =>
            option.externalLink
              ? window.open(option.link, "_blank")
              : navigate(option.link)
          }
        >
          {option.icon ? (
            <ListItemIcon sx={{ minWidth: "30px" }}>{option.icon}</ListItemIcon>
          ) : null}
          {/* <Box display='flex' alignItems='center'> */}
          <ListItemText
            primary={option.label}
            sx={{ pl: option.level * 3 }}
          ></ListItemText>

          <Badge badgeContent={option.badge} color="primary" />
          {option.externalLink && <OpenInNewIcon fontSize="small" />}
        </ListItemButton>
      </ConditionalWrapper>
    </>
  );
};

const renderSidebar = (options: any) => {
  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  if (options.children && options.children.length > 0) {
    return (
      <div key={options.label}>
        <ListItemButton onClick={handleClick}>
          {options.icon ? (
            <ListItemIcon sx={{ minWidth: "30px" }}>
              {options.icon}
            </ListItemIcon>
          ) : null}

          <ListItemText
            primary={options.label}
            sx={{ pl: options.level * 3 }}
          />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {options.children.map((child: any) => {
              return renderSidebar(child);
            })}
          </List>
        </Collapse>
      </div>
    );
  } else {
    return <div key={options.label}>{buildListLeaf(options)}</div>;
  }
};

export default function PersistentDrawerLeft(props: {
  children?: React.ReactNode;
}) {
  const { children } = props;
  const { authenticated, isLoading, isBusiness } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(!isMobile && authenticated);
  const page = window.location.pathname.split("/")[2];

  React.useEffect(() => {
    if (!authenticated) setOpen(false);
  }, [authenticated, isLoading]);

  const location = useLocation();

  return (
    <Box sx={{ display: "flex" }}>
      <ScrollRestoration />
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <PrimarySearchAppBar />
      </AppBar>

      <Main open={open}>
        <DrawerHeader />

        <Box
          sx={{
            display: "flex",
            width: "100%",
            minHeight: "100vh",
            justifyContent: "flex-start",
            flexDirection: "column",
            padding: [
              "/",
              "/welcome/loginForm",
              "/welcomeGovChamber/login",
              "/welcomeGovRegion/loginForm",
              "/welcome/ggpsIntro",
            ].includes(location.pathname)
              ? 0
              : "2rem",
          }}
        >
          {(import.meta.env.DEV || import.meta.env.MODE === "azureDev") && (
            <DemoEnvMsg />
          )}
          {isBusiness && page !== "acknowledge" && <CompanyInfo />}
          <GlobalMessage>
            {children}
            <Outlet />
          </GlobalMessage>
        </Box>

        <Footer open={open} drawerWidth={drawerWidth} />
      </Main>
    </Box>
  );
}
